import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PackageCheck, Check, X, Save, Landmark, Eye, Building2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminStockOrders() {
  const [orders, setOrders] = useState([]);
  const [prices, setPrices] = useState({ olaparty: "", trumeet: "" });
  const [savingPrices, setSavingPrices] = useState(false);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("pending");
  const [resellers, setResellers] = useState([]);
  const [rPrices, setRPrices] = useState({});
  const [savingR, setSavingR] = useState(null);
  const [bank, setBank] = useState({ account_name: "", account_number: "", ifsc: "", bank_name: "", upi_id: "", notes: "" });
  const [savingBank, setSavingBank] = useState(false);
  const [viewShot, setViewShot] = useState(null);

  const loadOrders = () => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/stock-orders${q}`).then(({ data }) => setOrders(data.orders || [])).catch(() => {});
  };
  const loadPrices = () => api.get("/admin/stock-prices").then(({ data }) => setPrices({ olaparty: data.prices.olaparty ?? "", trumeet: data.prices.trumeet ?? "" })).catch(() => {});
  const loadResellers = () => api.get("/admin/resellers").then(({ data }) => {
    const list = data.resellers || data || [];
    setResellers(list);
    const map = {};
    list.forEach((r) => { map[r.id] = { olaparty: r.stock_prices?.olaparty ?? "", trumeet: r.stock_prices?.trumeet ?? "" }; });
    setRPrices(map);
  }).catch(() => {});
  const loadBank = () => api.get("/admin/company-bank").then(({ data }) => setBank((b) => ({ ...b, ...(data.bank || {}) }))).catch(() => {});

  useEffect(() => { loadPrices(); loadResellers(); loadBank(); }, []);
  useEffect(() => { loadOrders(); }, [filter]);

  const savePrices = async () => {
    setSavingPrices(true);
    try {
      await api.put("/admin/stock-prices", { prices: { olaparty: parseFloat(prices.olaparty), trumeet: parseFloat(prices.trumeet) } });
      toast.success("Default stock prices updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSavingPrices(false); }
  };

  const saveResellerPrice = async (r) => {
    setSavingR(r.id);
    try {
      const v = rPrices[r.id] || {};
      await api.patch(`/admin/resellers/${r.id}/stock-prices`, { prices: {
        olaparty: v.olaparty === "" ? 0 : parseFloat(v.olaparty),
        trumeet: v.trumeet === "" ? 0 : parseFloat(v.trumeet),
      } });
      toast.success(`Saved rates for ${r.name || r.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSavingR(null); }
  };

  const saveBank = async () => {
    setSavingBank(true);
    try {
      await api.put("/admin/company-bank", bank);
      toast.success("Company bank details saved — resellers can now see them.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setSavingBank(false); }
  };

  const act = async (o, action) => {
    setBusy(o.id);
    try {
      await api.post(`/admin/stock-orders/${o.id}/${action}`);
      toast.success(action === "approve" ? `Approved · ${o.diamonds.toLocaleString()} 💎 credited` : "Order rejected");
      loadOrders();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-8" data-testid="admin-stock-orders">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Stock</p>
        <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Stock Orders</h1>
        <p className="text-zinc-500 mt-2 max-w-2xl">Set the default diamond price per platform and approve reseller stock purchases. Approving credits the reseller's allocation.</p>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="stock-prices-card">
        <h2 className="font-display text-lg font-medium text-zinc-900 mb-1">Default stock prices</h2>
        <p className="text-sm text-zinc-500 mb-4">Price a reseller pays per allocation diamond (₹). Override per reseller from a reseller's detail page.</p>
        <div className="flex flex-wrap items-end gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Olaparty (₹/💎)</Label>
            <Input type="number" step="0.01" min="0" value={prices.olaparty} onChange={(e) => setPrices((p) => ({ ...p, olaparty: e.target.value }))} className="w-40" data-testid="stock-price-olaparty" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Trumeet (₹/💎)</Label>
            <Input type="number" step="0.01" min="0" value={prices.trumeet} onChange={(e) => setPrices((p) => ({ ...p, trumeet: e.target.value }))} className="w-40" data-testid="stock-price-trumeet" />
          </div>
          <Button onClick={savePrices} disabled={savingPrices} className="bg-zinc-900 hover:bg-zinc-800" data-testid="stock-price-save"><Save className="w-4 h-4 mr-1" /> {savingPrices ? "Saving…" : "Save prices"}</Button>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="reseller-stock-prices-table">
        <h2 className="font-display text-lg font-medium text-zinc-900 mb-1">Per-reseller stock prices</h2>
        <p className="text-sm text-zinc-500 mb-4">Set a different ₹/💎 price for each reseller. Leave blank to use the platform default above.</p>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Reseller</Th><Th>Olaparty (₹/💎)</Th><Th>Trumeet (₹/💎)</Th><Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {resellers.map((r) => (
                <tr key={r.id} className="border-b border-zinc-100" data-testid={`reseller-price-row-${r.id}`}>
                  <Td><div className="font-medium text-zinc-900">{r.name || "—"}</div><div className="text-xs text-zinc-500">{r.email}</div></Td>
                  <Td><Input type="number" step="0.01" min="0" placeholder="default" value={rPrices[r.id]?.olaparty ?? ""} onChange={(e) => setRPrices((m) => ({ ...m, [r.id]: { ...m[r.id], olaparty: e.target.value } }))} className="w-28" data-testid={`reseller-price-olaparty-${r.id}`} /></Td>
                  <Td><Input type="number" step="0.01" min="0" placeholder="default" value={rPrices[r.id]?.trumeet ?? ""} onChange={(e) => setRPrices((m) => ({ ...m, [r.id]: { ...m[r.id], trumeet: e.target.value } }))} className="w-28" data-testid={`reseller-price-trumeet-${r.id}`} /></Td>
                  <Td className="text-right"><Button size="sm" variant="outline" disabled={savingR === r.id} onClick={() => saveResellerPrice(r)} data-testid={`reseller-price-save-${r.id}`}><Save className="w-3.5 h-3.5 mr-1" /> {savingR === r.id ? "…" : "Save"}</Button></Td>
                </tr>
              ))}
              {resellers.length === 0 && <tr><td colSpan="4" className="px-6 py-8 text-center text-sm text-zinc-400">No resellers yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="company-bank-card">
        <div className="flex items-center gap-2 mb-1"><Building2 className="w-4 h-4 text-zinc-500" /><h2 className="font-display text-lg font-medium text-zinc-900">Company bank details</h2></div>
        <p className="text-sm text-zinc-500 mb-4">Shown to resellers on the bank-transfer refill form so they know where to send funds.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Field label="Account name" v={bank.account_name} onChange={(v) => setBank((b) => ({ ...b, account_name: v }))} tid="bank-account-name" />
          <Field label="Account number" v={bank.account_number} onChange={(v) => setBank((b) => ({ ...b, account_number: v }))} tid="bank-account-number" />
          <Field label="IFSC" v={bank.ifsc} onChange={(v) => setBank((b) => ({ ...b, ifsc: v }))} tid="bank-ifsc" />
          <Field label="Bank name" v={bank.bank_name} onChange={(v) => setBank((b) => ({ ...b, bank_name: v }))} tid="bank-name" />
          <Field label="UPI ID" v={bank.upi_id} onChange={(v) => setBank((b) => ({ ...b, upi_id: v }))} tid="bank-upi" />
          <Field label="Notes (optional)" v={bank.notes} onChange={(v) => setBank((b) => ({ ...b, notes: v }))} tid="bank-notes" />
        </div>
        <Button onClick={saveBank} disabled={savingBank} className="mt-4 bg-zinc-900 hover:bg-zinc-800" data-testid="company-bank-save"><Landmark className="w-4 h-4 mr-1" /> {savingBank ? "Saving…" : "Save bank details"}</Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg" data-testid="stock-orders-card">
        <div className="p-6 border-b border-zinc-100 flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-display text-lg font-medium text-zinc-900">Orders</h2>
          <div className="flex gap-2">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`stock-filter-${f}`}
                className={`px-3 py-1.5 rounded-lg border text-xs font-medium capitalize transition-colors ${filter === f ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}>{f}</button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                <Th>Date</Th><Th>Reseller</Th><Th>Platform</Th><Th>Method</Th><Th className="text-right">Amount</Th>
                <Th className="text-right">Diamonds</Th><Th>Invoice</Th><Th>Status</Th><Th className="text-right">Action</Th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`stock-order-row-${o.id}`}>
                  <Td mono>{new Date(o.created_at).toLocaleDateString()}</Td>
                  <Td>
                    <Link to={`/admin/resellers/${o.reseller_id}`} className="font-medium text-zinc-900 hover:text-[#1F9D8A]">{o.reseller_email}</Link>
                  </Td>
                  <Td>{o.platform_name}</Td>
                  <Td>
                    {o.method === "bank_transfer" ? (
                      <button onClick={() => setViewShot(o)} className="inline-flex items-center gap-1 text-xs font-medium text-[#0E2A47] hover:underline" data-testid={`stock-order-view-${o.id}`}>
                        <Landmark className="w-3.5 h-3.5" /> {o.transfer_mode} <Eye className="w-3.5 h-3.5" />
                      </button>
                    ) : (
                      <span className="text-xs text-zinc-500">Razorpay</span>
                    )}
                  </Td>
                  <Td className="text-right font-mono">₹{(o.amount_inr ?? 0).toLocaleString()}</Td>
                  <Td className="text-right font-mono font-semibold text-zinc-900">{(o.diamonds ?? 0).toLocaleString()}</Td>
                  <Td mono>{o.invoice_number || "—"}</Td>
                  <Td><span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${badge(o.status)}`}>{o.status}</span></Td>
                  <Td className="text-right">
                    {o.status === "pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy === o.id} onClick={() => act(o, "approve")} data-testid={`stock-approve-${o.id}`}><Check className="w-4 h-4" /></Button>
                        <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700" disabled={busy === o.id} onClick={() => act(o, "reject")} data-testid={`stock-reject-${o.id}`}><X className="w-4 h-4" /></Button>
                      </div>
                    ) : <span className="text-xs text-zinc-400">{o.approved_by || "—"}</span>}
                  </Td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan="9" className="px-6 py-12 text-center text-sm text-zinc-400">No {filter === "all" ? "" : filter} stock orders.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!viewShot} onOpenChange={(o) => !o && setViewShot(null)}>
        <DialogContent className="max-w-lg" data-testid="stock-transfer-detail-dialog">
          <DialogHeader><DialogTitle>Bank transfer details</DialogTitle></DialogHeader>
          {viewShot && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Detail k="Reseller" v={viewShot.reseller_email} />
                <Detail k="Payer real name" v={viewShot.real_name} />
                <Detail k="Account number" v={viewShot.account_number} />
                <Detail k="Bank name" v={viewShot.bank_name || "—"} />
                <Detail k="IFSC" v={viewShot.ifsc || "—"} />
                <Detail k="Mode" v={viewShot.transfer_mode} />
                <Detail k="UTR number" v={viewShot.utr} />
                <Detail k="Amount" v={`₹${(viewShot.amount_inr ?? 0).toLocaleString()}`} />
                <Detail k="Diamonds" v={(viewShot.diamonds ?? 0).toLocaleString()} />
                <Detail k="Date / time" v={`${viewShot.transfer_date || ""} ${viewShot.transfer_time || ""}`} />
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1">Payment screenshot(s)</div>
                <div className="grid grid-cols-2 gap-2">
                  {Array.from({ length: Math.max(1, (viewShot.screenshot_paths || []).length) }).map((_, i) => (
                    <a key={i} href={`${API}/api/admin/stock-orders/${viewShot.id}/screenshot?i=${i}`} target="_blank" rel="noreferrer">
                      <img src={`${API}/api/admin/stock-orders/${viewShot.id}/screenshot?i=${i}`} alt={`payment screenshot ${i + 1}`} className="w-full rounded-lg border border-zinc-200" data-testid={i === 0 ? "stock-transfer-screenshot" : `stock-transfer-screenshot-${i}`} />
                    </a>
                  ))}
                </div>
              </div>
              {viewShot.status === "pending" && (
                <div className="flex gap-2 pt-1">
                  <Button className="bg-green-600 hover:bg-green-700 flex-1" disabled={busy === viewShot.id} onClick={() => { act(viewShot, "approve"); setViewShot(null); }} data-testid="stock-transfer-approve"><Check className="w-4 h-4 mr-1" /> Approve &amp; credit</Button>
                  <Button variant="outline" className="text-red-600 flex-1" disabled={busy === viewShot.id} onClick={() => { act(viewShot, "reject"); setViewShot(null); }} data-testid="stock-transfer-reject"><X className="w-4 h-4 mr-1" /> Reject</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function badge(s) {
  return { pending: "bg-amber-100 text-amber-800", approved: "bg-green-100 text-green-800", rejected: "bg-red-100 text-red-800", created: "bg-zinc-100 text-zinc-600", failed: "bg-red-100 text-red-800" }[s] || "bg-zinc-100 text-zinc-600";
}
const Th = ({ children, className = "" }) => (<th className={`text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3 ${className}`}>{children}</th>);
const Td = ({ children, className = "", mono }) => (<td className={`px-6 py-3 text-sm text-zinc-700 ${mono ? "font-mono" : ""} ${className}`}>{children}</td>);
const Field = ({ label, v, onChange, tid }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
    <Input value={v || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} />
  </div>
);
const Detail = ({ k, v }) => (
  <div><div className="text-[10px] uppercase tracking-wider text-zinc-400">{k}</div><div className="font-medium text-zinc-900 break-all">{v || "—"}</div></div>
);
